import React from 'react';
import * as IoC from 'typescript-ioc';
import { IntlShape } from 'react-intl';
import * as mobx from 'mobx';
import { v4 as uuidv4 } from 'uuid';
import { IAlertsService } from '../../../../../infrastructure/alerts';
import { alertServerError, showNotification } from '../../../../global/modal/alerts-host/alert-templates';
import Text from '../../../../global/text/text';
import { Space } from '../../../../../constants/html-character-entities';
import { IErrorParsing } from '../../../../../infrastructure/error-parsing';
import { IAttendeeDetailsDTO, IAttendeeOrganizationDTO, IAttendeesDAO, PodiumQuestionAnswerPriorityType, } from '../../../../../api/attendees.dao';
import { IGNORE_ATTENDEES, IOrganizationsDAO, IOrganizationsListDTO, } from '../../../../../api/organizations.dao';
import { EventExecutionRuntime } from '../../../../../domain/jobs/implementations/event-execution-runtime';
import { JobType } from '../../../../../api/jobs.dao';
import { LongRunningJob } from '../../../../../domain/jobs';
import { ISubWindowFacade, ISubWindowsService, SubWindowStatus } from '../../../../../infrastructure/sub-windows';
import { MANAGE_MAPPING_TEXT } from '../attendees.texts';
import { AttendeeFormDataViewModel } from './attendee-form-data.viewmodel';
import { AttendeeMappingForm } from './_attendee-mapping-form/attendee-mapping-form';
import { convertBoolTypeForMixPanel, isTrackingFieldEnabled, OpenFrom, TRACKING_EVENTS } from '../../../../../tracking-service/tracking-values';
import TrackingServiceProvider from '../../../../../tracking-service/tracking-service-provider';

enum ERRORS {
    EMAIL_NO_UNIQUE = 'EMAIL_NO_UNIQUE',
    EXTERNAL_ATTENDEE_ID_NO_UNIQUE = 'EXTERNAL_ATTENDEE_ID_NO_UNIQUE'
}

class AttendeeFormViewModel {
    @mobx.observable
    private isShownAlert: boolean;
    @mobx.observable
    private attendeeFormDataVM: AttendeeFormDataViewModel;
    @mobx.observable
    private isChecked: boolean;
    @mobx.observable
    private existingEmailError: string;
    @mobx.observable
    private existingExternalAttendeeIdError: string;
    @mobx.observable
    public validate: boolean;
    private setDeleteAttendeeAlertKey: (key: string) => void;
    public createOrganizationFromForm: (callback?: (shouldUpdateOrganizatios: boolean) => Promise<IOrganizationsListDTO>) => void;
    @mobx.observable
    private initialAttendeeValues: IAttendeeDetailsDTO;
    private manageMappingPopup: ISubWindowFacade;
    @mobx.observable
    private contactId: number = null;

    @mobx.observable
    private saving: boolean;

    constructor(
        private readonly intl: Readonly<IntlShape>,
        private deleteAttendeeOnList: () => void,
        setDeleteAttendeeAlertKey: (key: string) => void,
        createOrganizationFromForm: (callback?: (shouldUpdateOrganizatiosList: boolean) => Promise<IOrganizationsListDTO>) => void,
        private readonly accountId: number,
        private readonly eventId: number,
        private readonly openFrom: OpenFrom,
        private readonly attendeeId?: number,
        private saveCompletedCallback?: (attendee: IAttendeeDetailsDTO) => void,
        private readonly organizationId?: number,
        @IoC.Inject private readonly attendeesDAO: IAttendeesDAO = IoC.Container.get(IAttendeesDAO),
        @IoC.Inject private readonly organizationDAO: IOrganizationsDAO = IoC.Container.get(IOrganizationsDAO),
        @IoC.Inject private readonly errorParsingService: IErrorParsing = IoC.Container.get(IErrorParsing),
        @IoC.Inject private readonly alertsService: IAlertsService = IoC.Container.get(IAlertsService),
        @IoC.Inject private readonly jobsPipeline = IoC.Container.get(EventExecutionRuntime),
        @IoC.Inject private readonly trackingService: TrackingServiceProvider = IoC.Container.get(TrackingServiceProvider),
        @IoC.Inject private readonly subWindowsService: ISubWindowsService = IoC.Container.get(ISubWindowsService),
    ) {
        this.setDeleteAttendeeAlertKey = setDeleteAttendeeAlertKey;
        this.createOrganizationFromForm = createOrganizationFromForm;
        this.attendeeFormDataVM = null;
        this.existingEmailError = null;
        this.existingExternalAttendeeIdError = null;
        this.isChecked = false;

        this.jobsPipeline.onComplete(JobType.DeleteAttendee, () => {
            if (this.saveCompletedCallback) this.saveCompletedCallback.bind(this);
        });
    }

    @mobx.action
    public async initialize(validate: boolean) {
        this.validate = validate;
        this.attendeeFormDataVM = null;
        return Promise.all([this.fetchAttendee(), this.fetchOrganizations()]).then(fetchResult => {
            this.handleFormDataLoaded(fetchResult[1].content, fetchResult[0]);
            mobx.runInAction(() => {
                this.isChecked = true;
            });
        }).catch(e => {
            const errorMessage = this.errorParsingService.extractMessage(e);
            alertServerError('Error', `Unable to fetch form data, please retry your action. ${errorMessage}`);
        });
    }

    public returnIsChecked = () => this.isChecked;

    public getAttendeeFormDataVM() {
        return this.attendeeFormDataVM;
    }

    public getIsShownAlert = () => {
        return this.isShownAlert;
    };

    @mobx.action
    public formSubmitAction = async () => {
        if (!this.validate) {
            this.validate = true;
        }
        if (!this.attendeeFormDataVM.checkForm() || this.getExistingEmailError() || this.getExistingExternalAttendeeIdError()) {
            return;
        }
        return await this.performSaveCall();
    };

    public isSaving() {
        return this.saving;
    }

    public getAttendeeId() {
        return this.attendeeId;
    }

    public getContactId = (): number | null => this.contactId;

    @mobx.action
    private setContactId = (contactId: number | null): void => {
        this.contactId = contactId;
    };

    @mobx.action
    public openManageMappingPopup = (): void => {
        if (this.manageMappingPopup && this.manageMappingPopup.getStatus() !== SubWindowStatus.Closed) {
            this.manageMappingPopup.maximize();
            return;
        }

        this.manageMappingPopup = this.subWindowsService.showSubWindow({
            title: this.intl.formatMessage(MANAGE_MAPPING_TEXT),
            componentToHost: AttendeeMappingForm,
            componentToHostProps: {
                accountId: this.accountId,
                eventId: this.eventId,
                attendeeId: this.attendeeId,
                contactId: this.contactId,
                onMappingUpdated: this.setContactId,
            },
        });
    };

    public get deleteAttendeeJob(): LongRunningJob {
        return this.jobsPipeline.getJobByType(JobType.DeleteAttendee);
    }

    public getExistingEmailError = () => {
        return this.existingEmailError;
    };

    public getExistingExternalAttendeeIdError = () => {
        return this.existingExternalAttendeeIdError;
    };

    public showErrorMessages = () => {
        return this.isChecked && this.validate;
    };

    public isMailInValid(): boolean {
        return !!this.attendeeFormDataVM.getEmailError(this.attendeeFormDataVM.email, true);
    }

    public shouldShowEmailValidationError(): boolean {
        return this.isMailInValid() && this.attendeeFormDataVM.isMailTouched || this.showErrorMessages();
    }

    @mobx.action
    public refreshEmailError = () => {
        mobx.runInAction(() => {
            this.existingEmailError = null;
        });
    };

    public deleteAttendee = () => {
        this.alertsService.confirm({
            message: (
                <>
                    <Text
                        className='alert-message'
                        id='oe.alerts.areYouSure'
                        description='Alert message'
                        message='Are you sure you want to'
                    />
                    <Space />
                    <Text
                        className='alert-message--danger'
                        id='oe.meetings.deleteThisAttendee'
                        description='Alert message'
                        message='delete this Attendee'
                    />?<br /><br />
                    <Text
                        className='alert-message'
                        id='oe.meetings.thisAttendeeWillBeDeletedFromTheEventAndFromAllMeetings.'
                        description='Alert message'
                        message='This Attendee will be deleted from the event and from ALL meetings.'
                    /><br /><br />
                    <Text
                        className='alert-message'
                        id='oe.alerts.thisDeletionWillBePermanentAnd'
                        description='Alert message'
                        message='This deletion will be permanent and'
                    />
                    <Space />
                    <Text
                        className='alert-message--danger'
                        id='oe.alerts.cannotBeUndone.'
                        description='Alert message'
                        message='cannot be undone.'
                    />
                </>
            ),
            confirmButtonProps: {
                text: 'Yes, Delete',
                modifier: 'confirm-warn',
                handler: this.deleteAttendeeApiCall,
            },
            cancelButtonProps: {
                modifier: 'cancel'
            }
        });
    };

    @mobx.action
    public handleEmailsNotProvidedCheckbox = () => {
        this.attendeeFormDataVM.emailNotProvided = !this.attendeeFormDataVM.emailNotProvided;
        if (this.attendeeFormDataVM.emailNotProvided && !this.attendeeFormDataVM.email) {
            this.attendeeFormDataVM.email = this.generateDummyEmail();
        }
    };

    private generateDummyEmail() {
        const uuid = uuidv4();
        return `no-reply+${uuid}@open-exchange.net`;
    }

    private deleteAttendeeApiCall = async (): Promise<void> => {
        const job = this.deleteAttendeeJob;
        if (this.saveCompletedCallback) job.onComplete(this.saveCompletedCallback.bind(this));

        await job.trigger({ attendeeId: this.attendeeId });
        mobx.runInAction(() => this.deleteAttendeeOnList());
    };


    @mobx.action
    public refreshExternalAttendeeIdError = () => {
        mobx.runInAction(() => {
            this.existingExternalAttendeeIdError = null;
        });
    };

    private async performSaveCall() {
        try {
            this.saving = true;
            const params = {
                ...this.attendeeFormDataVM.toUpdateAPIDTO(),
                eventId: this.eventId,
                accountId: this.accountId
            };

            let response: IAttendeeDetailsDTO;

            if (this.attendeeId) {
                if (this.attendeeFormDataVM.isAttendeeNewRegistrationStatus()) {
                    params.sendRegistrationEmail = this.attendeeFormDataVM.sendRegistrationEmail;
                }
                response = await this.attendeesDAO.updateAttendee(params);
                showNotification('Attendee details updated');

                this.trackEditAttendeeMixPanel({
                    ...response,
                    profileImage: params.profileImage,
                });
            }
            else {
                response = await this.attendeesDAO.createAttendee(params);
                showNotification('New attendee added');

                this.trackCreateAttendeeMixPanel(response);
            }

            this.saveCompletedCallback?.(response);
            return true;
        } catch (e) {
            const response = JSON.parse(e.rawResponse);
            if (Array.isArray(response.fields)) {
                response.fields.forEach(error => {
                    if (error.name === ERRORS.EMAIL_NO_UNIQUE) {
                        mobx.runInAction(() => {
                            this.existingEmailError = error.message;
                        });
                    }
                    if (error.name === ERRORS.EXTERNAL_ATTENDEE_ID_NO_UNIQUE) {
                        mobx.runInAction(() => {
                            this.existingExternalAttendeeIdError = error.message;
                        });
                    }
                });
            } else {
                alertServerError('API error updating an attendee', this.errorParsingService.extractMessage(e));
            }
            return false;
        } finally {
            mobx.runInAction(() => {
                this.saving = false;
            });
        }
    }

    @mobx.action
    private handleFormDataLoaded(organizations: IAttendeeOrganizationDTO[], attendee?: IAttendeeDetailsDTO) {
        this.attendeeFormDataVM = new AttendeeFormDataViewModel(organizations, this.intl, this.accountId, this.eventId, attendee, this.organizationId);
        this.initialAttendeeValues = attendee;
        this.contactId = attendee?.oeContactId ?? null;
    }

    @mobx.action
    private async fetchAttendee() {
        return this.attendeeId ? await this.attendeesDAO.getAttendee({
            accountId: this.accountId,
            eventId: this.eventId,
            attendeeId: this.attendeeId,
        }) : null;
    }

    @mobx.action
    public async fetchOrganizations(shouldInitializeAttendeeFormDataVM?: boolean) {
        const newOrganizations = await this.organizationDAO.getAllOrganizations({
            eventId: this.eventId,
            accountId: this.accountId,
            ignoreAttendees: IGNORE_ATTENDEES
        });
        if (shouldInitializeAttendeeFormDataVM) {
            this.attendeeFormDataVM.updateOrganizationsList(newOrganizations.content);
        }
        return newOrganizations;
    }

    public trackCreateAttendeeMixPanel = (response: IAttendeeDetailsDTO) => {
        this.trackingService.get().track(TRACKING_EVENTS.NEW_ATTENDEE, {
            Organization: convertBoolTypeForMixPanel(!!response.organizationName),
            'First name': convertBoolTypeForMixPanel(!!response.firstName),
            'Last Name': convertBoolTypeForMixPanel(!!response.lastName),
            Email: convertBoolTypeForMixPanel(!!response.email),
            'Not provided': convertBoolTypeForMixPanel(!!response.emailNotProvided),
            Title: convertBoolTypeForMixPanel(!!response.title),
            Salutation: convertBoolTypeForMixPanel(!!response.salutation),
            'Phone Number': convertBoolTypeForMixPanel(!!response.phoneNumber),
            'Access Code': convertBoolTypeForMixPanel(!!response.accessCode),
            'Registration Status': response.registrationStatus,
            'Priority in organisation': convertBoolTypeForMixPanel(response.priorityInOrganization != null),
            'External attendee ID': convertBoolTypeForMixPanel(!!response.externalAttendeeId),
            'Attendee is a speaker': isTrackingFieldEnabled(response.speaker),
            'Profile Image': convertBoolTypeForMixPanel(!!response.profileImage),
            Biography: convertBoolTypeForMixPanel(!!response.biography),
            'Open From': this.openFrom,
            'Q&A Priority': this.convertPodiumQAPriorityToTrackingValue(response.podiumQuestionAnswerPriorityType)
        });
    };


    public trackEditAttendeeMixPanel = (response: IAttendeeDetailsDTO) => {
        this.trackingService.get().track(TRACKING_EVENTS.EDIT_ATTENDEE, {
            Organization: convertBoolTypeForMixPanel(
                response.organizationName !== this.initialAttendeeValues.organizationName
            ),
            'First name': convertBoolTypeForMixPanel(
                response.firstName !== this.initialAttendeeValues.firstName
            ),
            'Last Name': convertBoolTypeForMixPanel(response.lastName !== this.initialAttendeeValues.lastName),
            Email: convertBoolTypeForMixPanel(response.email !== this.initialAttendeeValues.email),
            'Not provided': convertBoolTypeForMixPanel(
                response.emailNotProvided !== this.initialAttendeeValues.emailNotProvided
            ),
            Title: convertBoolTypeForMixPanel(response.title !== this.initialAttendeeValues.title),
            Salutation: convertBoolTypeForMixPanel(
                response.salutation !== this.initialAttendeeValues.salutation
            ),
            'Phone Number': convertBoolTypeForMixPanel(
                response.phoneNumber !== this.initialAttendeeValues.phoneNumber
            ),
            'Access Code': convertBoolTypeForMixPanel(
                response.accessCode !== this.initialAttendeeValues.accessCode
            ),
            'Registration Status': response.registrationStatus,
            'Priority in organisation': convertBoolTypeForMixPanel(
                response.priorityInOrganization !== this.initialAttendeeValues.priorityInOrganization
            ),
            'External attendee ID': convertBoolTypeForMixPanel(
                response.externalAttendeeId !== this.initialAttendeeValues.externalAttendeeId
            ),
            'Attendee is a speaker': isTrackingFieldEnabled(response.speaker),
            'Profile Image': convertBoolTypeForMixPanel(
                response.profileImage?.objectKey !== this.initialAttendeeValues.profileImage?.objectKey
            ),
            Biography: convertBoolTypeForMixPanel(response.biography !== this.initialAttendeeValues.biography),
            'Open From': this.openFrom,
            'Q&A Priority': this.convertPodiumQAPriorityToTrackingValue(response.podiumQuestionAnswerPriorityType)
        });
    };

    private convertPodiumQAPriorityToTrackingValue(value: PodiumQuestionAnswerPriorityType | null): string | null {
        const trackValueMap = {
            [PodiumQuestionAnswerPriorityType.RED_FLAG]: 'Red Flag',
            [PodiumQuestionAnswerPriorityType.STAR]: 'Star',
        };

        return trackValueMap[value] || null;
    };
}

export { AttendeeFormViewModel, ERRORS };
